import { useEffect, useState } from 'react'
import { useAuth } from '@/modules/context/AuthContext'
import { useDialog } from '@/hooks/useDialog/useDialog'
import { Daycare, DaycarePredefinedMessageType } from '@/types/Daycare'
import PredefinedMessage from './predefinedMessage'
import { t } from 'i18next'

interface PredefinedMessageOptions {
    daycare?: Daycare
    allowEditDefault?: boolean
}

export function usePredefinedMessage(
    messageType: DaycarePredefinedMessageType,
    { daycare, allowEditDefault = true }: PredefinedMessageOptions = {}
) {
    const { user } = useAuth()
    const currentDaycare: Daycare | undefined = user?.currentDaycare
    const messageDaycare: Daycare | undefined = !daycare || daycare.id === currentDaycare?.id ? currentDaycare : daycare
    const defaultMessage: string =
        messageDaycare?.predefinedMessages?.find(({ type }) => type === messageType)?.message ?? ''
    const [message, setMessage] = useState(defaultMessage)

    // Preserve local edits unless the default or the message context changes.
    useEffect(() => {
        setMessage(defaultMessage)
    }, [defaultMessage, messageType, messageDaycare?.id])

    const { dialog, openDialog } = useDialog({
        Component: PredefinedMessage,
        props: (onClose) => ({ messageType, onClose }),
        dialogTitle: t('modifyDefaultMessage')
    })

    const canEditDefault: boolean =
        allowEditDefault &&
        !!currentDaycare &&
        messageDaycare?.id === currentDaycare.id &&
        !!user?.currentDaycareRights.canWriteParamsDaycare

    return {
        message,
        setMessage,
        dialog: canEditDefault ? dialog : null,
        textAreaProps: {
            value: message,
            onChange: setMessage,
            maxCount: 200,
            actionText: canEditDefault ? { label: 'modifyDefaultMessage', action: openDialog } : undefined
        }
    }
}
